<template>
  <b-card-actions
    no-actions
    :show-loading="loading"
    :title="$t('Controle de documentos')"
  >
    <b-row class="mb-2">
      <b-col class="d-flex justify-content-end">
        <e-button
          :text="$t('Adicionar documento')"
          variant="primary"
          @click="onAddPermitLicense"
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-table
          id="permit-licenses-table"
          ref="permit-licenses-table"
          class="table-responsive bordered"
          responsive
          striped
          show-empty
          :tbody-tr-class="rowClass"
          :items="paginatedPermitLicenses"
          :fields="fields"
        >
          <template #cell(actions)="row">
            <e-grid-actions
              v-if="!row.item.isLocal"
              :show-delete="true"
              :show-update="false"
              :buttons="[
                {
                  icon: 'download',
                  variant: 'primary',
                  title: $t('Baixar arquivo'),
                  event: 'download-file',
                },
              ]"
              @delete="onDeletePermitLicense(row.item)"
              @download-file="onDownloadFile(row)"
            />
            <e-grid-actions
              v-else
              :show-delete="true"
              :show-update="false"
              :buttons="[]"
              @delete="onDeleteLocalPermitLicense(row.item)"
            />
          </template>
          <template #cell(permitLicenseType)="row">
            {{ getPermitLicenseType(row.item.permitLicenseType) }}
            <span
              v-if="row.item.observation"
              :id="`permit-license-observation-${row.item.id || row.item.localId}`"
              class="text-primary cursor-pointer ml-50"
              tabindex="0"
            >
              <feather-icon icon="InfoIcon" />
            </span>
            <b-tooltip
              v-if="row.item.observation"
              :target="`permit-license-observation-${row.item.id || row.item.localId}`"
              :title="row.item.observation"
              placement="top"
              triggers="hover click"
            />
          </template>
          <template #cell(dueDate)="row">
            {{ row.item.dueDate | date }}
          </template>
        </b-table>
        <b-row>
          <b-col md="6">
            <e-pagination-summary
              :current-page="paging.currentPage"
              :per-page="paging.pageSize"
              :total="paging.rowCount"
              :total-on-page="paging.rowsInCurrentPage"
            />
          </b-col>
          <b-col md="6">
            <b-pagination
              v-model="paging.currentPage"
              align="right"
              :total-rows="paging.rowCount"
              :per-page="paging.pageSize"
              aria-controls="product-table"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <e-color-subtitled
              :colors="[
                {
                  backgroundColor: 'table-danger',
                  title: $t('Vencido'),
                }
              ]"
            />
          </b-col>
        </b-row>
      </b-col>

      <permit-licenses-sidebar
        ref="permitLicensesSidebar"
        :is-edition="isEdition"
      />
    </b-row>
  </b-card-actions>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { BTable, BRow, BCol, BPagination, BTooltip } from 'bootstrap-vue'
import EGridActions from '@/views/components/EGridActions.vue'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import EPaginationSummary from '@/views/components/pagination/EPaginationSummary.vue'
import EButton from '@/views/components/EButton.vue'
import { companyDomains, loadingOverlay, downloader } from '@/mixins'
import moment from 'moment'
import EColorSubtitled from '@/views/components/EColorSubtitled.vue'
import PermitLicensesSidebar from './PermitLicensesSidebar.vue'

export default {
  components: {
    BTable,
    EGridActions,
    BRow,
    BCol,
    BPagination,
    BTooltip,
    EPaginationSummary,
    EColorSubtitled,
    PermitLicensesSidebar,
    EButton,
    BCardActions,
  },
  mixins: [companyDomains, loadingOverlay, downloader],
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    isEdition: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      paging: {
        currentPage: 1,
        pageSize: 5,
        rowCount: 0,
        rowsInCurrentPage: 0,
      }
    }
  },
  computed: {
    ...mapState('pages/company/companyMaintain', ['company']),
    fields() {
      return [
        {
          key: 'actions',
          label: this.$t('Ações'),
          class: 'text-center',
          thStyle: { width: '100px' },
          hide: !this.isEdition,
        },
        {
          key: 'permitLicenseType',
          label: this.$t('Tipo do Documento'),
        },
        {
          key: 'dueDate',
          label: this.$t('Data de Vencimento'),
          thStyle: { minWidth: '150px', width: '150px' },
          class: 'text-center',
        },
      ]
    },
    permitLicensesTypesOptions() {
      return this.permitLicensesTypes()
    },
    paginatedPermitLicenses() {
      const permitLicenses = this.company.permitLicenses || []
      const start = (this.paging.currentPage - 1) * this.paging.pageSize
      const end = start + this.paging.pageSize
      return permitLicenses.slice(start, end)
    },
    totalPermitLicenses() {
      return (this.company.permitLicenses || []).length
    },
  },
  watch: {
    'paging.currentPage': {
      handler() {
        this.updatePagingInfo()
      },
    },
    'paging.pageSize': {
      handler() {
        this.paging.currentPage = 1
        this.updatePagingInfo()
      },
    },
    'company.permitLicenses': {
      handler() {
        this.updatePagingInfo()
      },
      deep: true
    }
  },
  mounted() {
    this.updatePagingInfo()
  },
  methods: {
    ...mapActions('pages/company/companyMaintain', [
      'downloadPermitLicenseDocument',
      'deletePermitLicense',
      'removePermitLicenseLocal',
    ]),
    updatePagingInfo() {
      this.paging.rowCount = this.totalPermitLicenses
      this.paging.rowsInCurrentPage = this.paginatedPermitLicenses.length
    },
    async onDownloadFile(row) {
      try {
        this.showLoadingOverlayV2({
          text: this.$t('Baixando'),
          cancelable: true,
        })
        const { item } = row

        const data = await this.downloadPermitLicenseDocument({ permitLicenseId: item.id })
        const nameFile = `Documento_${this.getPermitLicenseType(row.item.permitLicenseType)}_${moment.utc(row.item.dueDate).format('DD_MM_YYYY')}`

        this.forceFileDownloadFromBase64(data.permitLicenseInBase64, nameFile, 'pdf')
      } catch (error) {
        this.showError({ error })
      } finally {
        this.hideLoadingOverlay()
      }
    },
    rowClass(item, type) {
      if (!item || type !== 'row') return ''
      if (!moment.utc(item.dueDate).isBefore(moment.utc(), 'day')) {
        return ''
      }

      return 'table-danger'
    },
    onAddPermitLicense() {
      this.$refs.permitLicensesSidebar.show()
    },
    async onDeletePermitLicense(item) {
      const response = await this.confirm({
        text: this.$t('Tem certeza que deseja excluir este documento permanentemente?'),
      })

      if(!response) return

      try {
        this.loading = true
        await this.deletePermitLicense({ permitLicenseId: item.id })
      } catch (error) {
        this.showError({ error })
      } finally {
        this.loading = false
      }
    },
    async onDeleteLocalPermitLicense(item) {
      const response = await this.confirm({
        text: this.$t('Deseja remover este documento?'),
      })

      if(!response) return

      try {
        this.removePermitLicenseLocal(item.localId)
        this.showSuccess({ message: this.$t('Documento removido com sucesso') })
      } catch (error) {
        this.showError({ error })
      }
    }
  },
}
</script>
